import { FC, useMemo, useState } from 'react'

import { useParams } from 'react-router'

import InfiniteScrollDataTable from '../../../../../../components/common/tables/InfiniteScrollDataTable'

import { IBreakRegistrationByDay } from '../../../../../../models/events/event.interface'
import { IRegistrationByDayInterface } from '../../../../../../models/registrations-by-day/registrationByDay.interface'
import { IManageOption } from '../../types/IManageOption'
import { IEventFees } from '../../../../../../models/event-fees/event-fees.interface'
import { IRegistrationByDayEx } from '../../ManageClinicNOtherOperationsTab'

import { getColumnsScoring } from './data/columns'

import { getRideTime } from '../../../../../../helpers/time'
import { useEventRiderTeamMembers } from '../../../../../../hooks/useEventRiderTeamMembers'
import { sortByOrder } from '../../data/operations-tab-utils'

interface ScoringTableByRiderProps {
  selectedDay: IManageOption
  registrationsGroupedByDay: { [key: string]: IBreakRegistrationByDay[] }
  registrationsByDay: IRegistrationByDayInterface[]
  setRegistrationsByDay: (value: IRegistrationByDayInterface[]) => void
  searchValue: string
  registrationFees: IEventFees | null
  onScore: (value: IRegistrationByDayEx) => Promise<void>
  fileChangedHandler: (row: IRegistrationByDayEx, file: FileList | null) => Promise<void>
}

export const ScoringTableByRider: FC<ScoringTableByRiderProps> = ({
  selectedDay,
  registrationsByDay,
  setRegistrationsByDay,
  registrationsGroupedByDay,
  searchValue,
  registrationFees,
  onScore,
  fileChangedHandler,
}) => {
  const { eventId } = useParams<{ eventId: string }>()

  const { eventTeamMembers, eventTeamMembersLoading } = useEventRiderTeamMembers(eventId)

  const [scoringLoading, setScoringLoading] = useState('')
  const [uploadLoading, setUploadScoringLoading] = useState('')

  const registrationsToDisplay = useMemo(() => {
    let registrations = selectedDay ? [...(registrationsGroupedByDay[selectedDay.value] || [])] : []

    const registrationsByClass: Record<string, IRegistrationByDayInterface[]> = {}

    registrations.forEach((reg) => {
      const className = reg.registrationByDayName || ''
      if (!registrationsByClass[className]) {
        registrationsByClass[className] = []
      }
      registrationsByClass[className].push(reg as IRegistrationByDayInterface)
    })

    let sortedRegistrations: IRegistrationByDayInterface[] = []

    Object.entries(registrationsByClass).forEach(([_, classRegs]) => {
      sortedRegistrations.push(...classRegs)
    })

    // sort by scheduling order - same as order of go table by rider
    return sortByOrder(registrations as IRegistrationByDayInterface[]).map((registrationByDay) => {
      // find the reg with calculated rank per their class from sortedRegistrations
      const withRank = sortedRegistrations.find((r) => r.id === registrationByDay.id)
      return withRank || registrationByDay
    })
  }, [selectedDay, registrationsGroupedByDay])

  const getTime = (row: IRegistrationByDayInterface) => {
    return row.rideTime ? getRideTime(row.rideTime) : '00:00:00 AM'
  }

  return (
    <InfiniteScrollDataTable
      hasMore={false}
      className="exhibitorListTable"
      columns={getColumnsScoring({
        type: 'rider',
        eventTeamMembers,
        eventTeamMembersLoading,
        scoringLoading,
        uploadLoading,
        registrationsByDay,
        setRegistrationsByDay,
        registrationFees,
        getTime,
        onScore: async (row) => {
          setScoringLoading(row.id || '')
          await onScore(row)
          setScoringLoading('')
        },
        fileChangedHandler: async (row, file) => {
          setUploadScoringLoading(row.id || '')
          await fileChangedHandler(row, file)
          setUploadScoringLoading('')
        },
      })}
      data={registrationsToDisplay.filter((item) => {
        if (!searchValue?.trim()) return true
        const searchLower = searchValue.toLowerCase()
        const riderName = item.riderName?.toLowerCase() || ''
        return riderName.includes(searchLower)
      })}
    />
  )
}
